import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Dead reckoning hook — extrapolates a driver marker's position forward
 * between server updates using the last known speed and heading.
 *
 * Returns an animated { lat, lng } that updates on every animation frame
 * while the driver is moving, then snaps to the server position when
 * new data arrives.
 *
 * When a `routePath` is provided (decoded polyline from the active move),
 * the extrapolated position is snapped to the nearest point on the route
 * so the marker follows the road instead of drifting in a straight line.
 *
 * Constraints:
 *  - Minimum speed threshold: 1 m/s (~2.2 mph) — below this we assume stationary
 *  - Max extrapolation: 30 seconds — after that we stop projecting to avoid drift
 *  - Respects `prefers-reduced-motion` — disables animation entirely
 */

// Earth radius in meters
const EARTH_RADIUS_M = 6_371_000;

// Stop extrapolating after 30 seconds without a server update
const MAX_EXTRAPOLATION_MS = 30_000;

// Below this speed (m/s) the driver is considered stationary
const MIN_SPEED_MPS = 1;

// Max distance from route (meters) before we skip snapping (driver may be off-route)
const MAX_SNAP_DISTANCE_M = 200;

interface LatLng {
  lat: number;
  lng: number;
}

interface DeadReckoningInput {
  /** Server-reported latitude */
  lat: number;
  /** Server-reported longitude */
  lng: number;
  /** Speed in m/s (null/undefined = stationary) */
  speed?: number | null;
  /** Heading in degrees clockwise from north (null/undefined = unknown) */
  heading?: number | null;
  /** Whether the driver is currently moving */
  isMoving?: boolean | null;
  /** ISO timestamp of the server reading */
  timestamp?: string | null;
  /** Decoded polyline from the active move's route — enables road-snapping */
  routePath?: LatLng[] | null;
}

interface DeadReckoningResult {
  lat: number;
  lng: number;
}

/**
 * Given a starting lat/lng, bearing (degrees), and distance (meters),
 * compute the destination point on a sphere.
 */
function destinationPoint(
  lat: number,
  lng: number,
  bearingDeg: number,
  distanceM: number,
): LatLng {
  const toRad = Math.PI / 180;
  const toDeg = 180 / Math.PI;

  const lat1 = lat * toRad;
  const lng1 = lng * toRad;
  const bearing = bearingDeg * toRad;
  const angularDist = distanceM / EARTH_RADIUS_M;

  const sinLat1 = Math.sin(lat1);
  const cosLat1 = Math.cos(lat1);
  const sinAng = Math.sin(angularDist);
  const cosAng = Math.cos(angularDist);

  const lat2 = Math.asin(sinLat1 * cosAng + cosLat1 * sinAng * Math.cos(bearing));
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * sinAng * cosLat1,
      cosAng - sinLat1 * Math.sin(lat2),
    );

  return { lat: lat2 * toDeg, lng: lng2 * toDeg };
}

/**
 * Approximate distance between two lat/lng points in meters (Haversine).
 */
function haversineDistance(a: LatLng, b: LatLng): number {
  const toRad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toRad;
  const dLng = (b.lng - a.lng) * toRad;
  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLng = Math.sin(dLng / 2);
  const h =
    sinHalfLat * sinHalfLat +
    Math.cos(a.lat * toRad) * Math.cos(b.lat * toRad) * sinHalfLng * sinHalfLng;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/**
 * Project point P onto segment A→B, returning the closest point on the segment.
 * Uses flat-earth approximation (adequate for short segments).
 */
function projectOntoSegment(p: LatLng, a: LatLng, b: LatLng): LatLng {
  const dx = b.lng - a.lng;
  const dy = b.lat - a.lat;
  if (dx === 0 && dy === 0) return a;

  let t = ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
  t = Math.max(0, Math.min(1, t));

  return {
    lat: a.lat + t * dy,
    lng: a.lng + t * dx,
  };
}

/**
 * Snap a point to the nearest location on a polyline path.
 * Returns the snapped point, or null if the point is more than
 * MAX_SNAP_DISTANCE_M from the route (driver is off-route).
 */
function snapToPolyline(point: LatLng, path: LatLng[]): LatLng | null {
  if (path.length < 2) return null;

  let bestPoint: LatLng = path[0];
  let bestDist = Infinity;

  for (let i = 0; i < path.length - 1; i++) {
    const projected = projectOntoSegment(point, path[i], path[i + 1]);
    const dist = haversineDistance(point, projected);
    if (dist < bestDist) {
      bestDist = dist;
      bestPoint = projected;
    }
  }

  // If too far from the route, don't snap — the driver may be off-route
  if (bestDist > MAX_SNAP_DISTANCE_M) return null;

  return bestPoint;
}

export function useDeadReckoning({
  lat,
  lng,
  speed,
  heading,
  isMoving,
  timestamp,
  routePath,
}: DeadReckoningInput): DeadReckoningResult {
  const [position, setPosition] = useState<DeadReckoningResult>({ lat, lng });
  const frameRef = useRef<number>(0);
  const serverTimeRef = useRef<number>(Date.now());

  // Check prefers-reduced-motion once on mount
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Determine if we should animate
  const shouldAnimate =
    !prefersReducedMotion.current &&
    isMoving === true &&
    speed != null &&
    speed >= MIN_SPEED_MPS &&
    heading != null &&
    timestamp != null;

  // Track server timestamp for age calculation
  useEffect(() => {
    if (timestamp) {
      serverTimeRef.current = new Date(timestamp).getTime();
    }
  }, [timestamp]);

  // Snap to server position when it changes (new data arrived)
  useEffect(() => {
    setPosition({ lat, lng });
  }, [lat, lng]);

  const animate = useCallback(() => {
    if (!shouldAnimate) return;

    const elapsedMs = Date.now() - serverTimeRef.current;

    // Stop extrapolating after MAX_EXTRAPOLATION_MS
    if (elapsedMs > MAX_EXTRAPOLATION_MS) return;

    const distanceM = (speed as number) * (elapsedMs / 1000);
    let dest = destinationPoint(lat, lng, heading as number, distanceM);

    // Snap to route polyline if available
    if (routePath && routePath.length >= 2) {
      const snapped = snapToPolyline(dest, routePath);
      if (snapped) dest = snapped;
    }

    setPosition(dest);

    frameRef.current = requestAnimationFrame(animate);
  }, [shouldAnimate, lat, lng, speed, heading, routePath]);

  useEffect(() => {
    if (!shouldAnimate) {
      // Not animating — ensure we're at the server position
      setPosition({ lat, lng });
      return;
    }

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [shouldAnimate, animate, lat, lng]);

  return position;
}
